/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable react/no-danger */
import React, { ReactNode, useEffect, useRef, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import useLoadCaptcha from '@/hooks/useLoadCaptcha';
import { useHTTPError } from 'br2s-ui-common-utils-02';
import {
  Button,
  FileInfo,
  InlineAlert,
  TextArea,
  TextField,
  TextFieldInput,
  Typography,
} from 'br2s-ui-componentlibrary_r2_02';
import { CRM_FORM } from '@/services/api/crm/crm';
import {
  getInputState,
  isAttachmentsLoadingOrError,
  renderFileUploader,
  modifyQueryParam,
  generateSession,
} from '../crmUtilities/crmUtils';
import {
  NO_SPECIAL_CHARS_REGEX,
  CLARIFICATION_INPUT_ERROR,
} from '../crmConstants';

import '../crm-form.scss';
import {isAxiosError } from 'axios';

type FormError = {
  [key: string]: string;
};

type Alert = {
  type: 'error' | 'success' | 'informative' | 'warning';
  message: ReactNode;
};

const RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY;

const CRMClarificationForm = () => {
  useLoadCaptcha();

  const { handleHTTPError } = useHTTPError();

  const isFirstRender = useRef(true);
  const [clarificationFormData, setClarificationFormData] = useState<any>({});
  const [formValue, setFormValue] = useState({
    comment: '',
  });
  const [attachments, setAttachments] = useState<FileInfo[]>([]);
  const [formError, setFormError] = useState<FormError>({});
  const [isSubmitClicked, setIsSubmitClicked] = useState(false);
  const [alert, setAlert] = useState<Alert>({
    type: 'informative',
    message: '',
  });
  const [isSubmitDisabled, setIsSubmitDisabled] = useState(true);
  const [isFormHidden, setIsFormHidden] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [data, setData] = useState('');

  useEffect(() => {
    if (isFirstRender.current) {
      fetchSession();    
      isFirstRender.current = false;
      return () => window.sessionStorage.removeItem('transactionId');
    }
  }, []);

  // istanbul ignore next
  const fetchSession = async () => {
    try {
      await generateSession();
      formLoad();
    } catch (error) {
      console.error(error);
      if (isAxiosError(error) && error.response?.status !== 400) {
        setErrorMessage (error.response?.data?.error?.errorDesc)
        setIsFormHidden (true);
      }       
    }
  };

  const formLoad = async () => {
    console.log(data);
    window.sessionStorage.setItem('transactionId', uuidv4());
    const encryptedFormParam = modifyQueryParam();
    try {
      const response = await CRM_FORM.getClarification(encryptedFormParam);
      if (response.status === 200) {
        setClarificationFormData(response.data.result);
        setIsSubmitDisabled(false);
        setData(response.data.result);
      }
    } catch (error: any) {
      console.error(error);
      if (isAxiosError(error) && error.response?.status !== 400) {
        setErrorMessage (`${process.env.REACT_CRM_API_DEFAULT_ERROR_MESSAGE}`)
        setIsFormHidden (true);
      }   
      setIsSubmitDisabled(true);
    }
  };

  const setFormEmptyError = () => {
    if (!alert.message) {
      setAlert({
        type: 'error',
        message: 'Please fill in the required fields',
      });
    }
  };

  const removeError = (key: string) => {
    setFormError((prevState) => {
      const temp = { ...prevState };
      delete temp[key];
      return temp;
    });
  };

  const handleInputChange = (key: string, val: string) => {
    setFormValue((prevState) => ({ ...prevState, [key]: val }));
  };

  const handleSubmit = async () => {
    setIsSubmitClicked(true);

    const isFormValid = validateForm();
    if (!isFormValid) {
      return;
    }

    setIsSubmitDisabled(true);

    // @ts-ignore
    window.grecaptcha.ready(() => {
      // @ts-ignore
      window.grecaptcha
        .execute(RECAPTCHA_SITE_KEY, { action: 'submit' })
        .then(async (captchaToken: string) => {
          try {
            const payload = {
              requestTransactId:
                window.sessionStorage.getItem('transactionId') || '',
              webformParameter: modifyQueryParam(),
              caseComment: formValue.comment,
              attachmentList: attachments.length
                ? attachments.map((attachment) => ({
                    '@odata.type': 'Microsoft.Dynamics.CRM.acra_document',
                    acra_uuidtext: attachment.successData.uuid,
                    acra_filenametext: attachment.successData.name,
                  }))
                : null,
            };
            const response = await CRM_FORM.submitClarification(
              payload,
              captchaToken
            );

            if (response?.status === 200) {
              setAlert({
                type: 'success',
                message: 'Success: Your clarification has been submitted.',
              });
              resetClarificationForm();
            }
          } catch (error: any) {
            console.error(error);
            handleHTTPError(error, {
              errorType: 'inline',
            });
            setIsSubmitDisabled(false);
          }
        });
    });
  };

  const validateComment = (commentVal: string) => {
    let isValid = true;
    if (commentVal === '') {
      setFormError((prevState) => ({
        ...prevState,
        comment: 'required',
      }));
      setFormEmptyError();
      isValid = false;
    } else if (
      !NO_SPECIAL_CHARS_REGEX.test(commentVal) ||
      commentVal.includes('.exe') ||
      commentVal.includes('.sh')
    ) {
      setFormError((prevState) => ({
        ...prevState,
        comment: 'specialChars',
      }));
      isValid = false;
    } else {
      removeError('comment');
    }
    return isValid;
  };

  const validateForm = () => {
    let isValid = true;
    if (!validateComment(formValue.comment)) {
      isValid = false;
    }
    if (isValid) {
      if (alert.message) {
        setAlert({
          type: 'informative',
          message: '',
        });
      }
    }
    return isValid;
  };

  const resetClarificationForm = () => {
    setFormValue((prevState) => ({
      ...prevState,
      comment: '',
    }));
    setAttachments([]);
    setFormError({});
    setIsSubmitClicked(false);
    document.body.scrollTop = 0;
    document.documentElement.scrollTop = 0;
    window.sessionStorage.setItem('transactionId', uuidv4());
  };

  return (    
    <div className="crm-form-wrapper">
       { isFormHidden && (
        <InlineAlert
        id="inline-alert-introduction"
        headline={'Error'}
        subHeadline=""
        type="error"
      >
        <Typography type="base" label={errorMessage} />
      </InlineAlert>
      )}
      { !isFormHidden && data !== '' && (
      <div className="container crm-form">
        {alert.message && (
          <InlineAlert
            type={alert.type}
            // @ts-ignore
            headline={alert.message}
          />
        )}
        <TextField id="name" label="Customer Name" state="disabled">
          <TextFieldInput
            id="name"
            name="name"
            value={clarificationFormData?.caseCustomerName}
            isDisabled
          />
        </TextField>
        <TextField id="entity" label="Entity Name" state="disabled">
          <TextFieldInput
            id="entity"
            name="entity"
            value={clarificationFormData?.caseEntityName}
            isDisabled
          />
        </TextField>
        <TextField id="uen" label="UEN" state="disabled">
          <TextFieldInput
            id="uen"
            name="uen"
            value={clarificationFormData?.caseUEN}
            isDisabled
          />
        </TextField>
        <TextArea
          id="comment"
          name="comment"
          label="Message"
          value={formValue.comment}
          onChange={(event) => {
            handleInputChange('comment', event.target.value);
            validateComment(event.target.value);
          }}
          maxLength={3000}
          state={getInputState('comment', formError, isSubmitClicked)}
          helperText={
            (CLARIFICATION_INPUT_ERROR.comment as Record<string, string>)[
              formError.comment
            ]
          }
        />
        {renderFileUploader(attachments, setAttachments)}
        <Button
          id="submit"
          label="Submit"
          onClick={handleSubmit}
          isBlock
          isDisabled={
            isSubmitDisabled || isAttachmentsLoadingOrError(attachments)
          }
        />
      </div>
      )}     
    </div>
  );
};

export default CRMClarificationForm;
